import type { ProductZoomPanelHandle } from './ProductZoomPanel';
import type { ProductImageGallery as ProductImageGallerySchema } from '../../../../amplienceTypes/schemas/imported/product-image-gallery-schema';
import type { ProductVideo as ProductVideoSchema } from '../../../../amplienceTypes/schemas/imported/product-video-schema';
import type { FC, MouseEvent, PropsWithChildren } from 'react';
import type { RuleSet } from 'styled-components';

import dynamic from 'next/dynamic';
import { useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';

import { ProductGalleryShowMoreButton } from './ProductGalleryShowMoreButton';
import { Signings } from '../../signings/Signings';
import { ProductCarouselVideoView } from '~/components/pdp/productDetail/productImageGallery/ProductCarouselVideoView';
import { ProductScrolledView } from '~/components/pdp/productDetail/productImageGallery/ProductScrolledView';
import { getRowPattern } from '~/components/pdp/productDetail/productImageGallery/utils/rowPattern';
import { breakpoints, media } from '~/components/shared/core/styles';
import { CSSLargeScreenOnly } from '~/components/shared/CSSLargeScreenOnly';
import { CSSSmallScreenOnly } from '~/components/shared/CSSSmallScreenOnly';
import { FeatureFlag } from '~/components/shared/FeatureFlag';
import { CodeFeatureFlag } from '~/config/code-feature-flags';
import { MOBILE_VIDEO_POSITION, DEFAULT_LARGE_DESKTOP_BREAKPOINT } from '~/utilities/constants';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useMediaQueryContext } from '~/utilities/context/dynamic/MediaQueryContext';
import { useNavActionContext } from '~/utilities/context/dynamic/NavActionContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { VideoMode, SiteType } from '~/utilities/graphql/codegen';
import { useProductDetailsImages } from '~/utilities/hooks/useProductDetailsImages';

const ProductImageGalleryGStarStyles: RuleSet = css`
  cursor: url('/_fes/${process.env.BUILD_TIME}/img/zoom/zoom-in-cursor-square.cur') 12 12, zoom-in;
`;

const ProductImageGalleryAAFStyles: RuleSet = css`
  cursor: url('/_fes/${process.env.BUILD_TIME}/img/zoom/zoom-in-cursor.cur') 18 18, default;
`;

const ProductImageGalleryWahtsStyles: RuleSet = css`
  --image-background-placeholder: ${({ theme }) => theme.colors.neutral[50]};

  cursor: url('/_fes/${process.env.BUILD_TIME}/img/zoom/zoom-in-cursor-square.cur') 12 12, zoom-in;
  background-color: ${({ theme }) => theme.colors.neutral[50]};
`;

const ProductImageGalleryStyles: Record<SiteType, RuleSet> = {
  [SiteType.Aaf]: ProductImageGalleryAAFStyles,
  [SiteType.Employeeshop]: ProductImageGalleryGStarStyles,
  [SiteType.Gstar]: ProductImageGalleryGStarStyles,
  [SiteType.Outlet]: ProductImageGalleryGStarStyles,
  [SiteType.Wahts]: ProductImageGalleryWahtsStyles,
};

const ProductZoomPanel = dynamic(
  async () => (await import('./ProductZoomPanel')).ProductZoomPanel,
  { ssr: false }
);

const JumpLink = dynamic(
  async () => (await import('~/components/shared/recommendations/stylitics/JumpLink')).JumpLink,
  { ssr: false }
);

export interface ProductImageGalleryHybrisProps {
  productVideo: {
    content: ProductVideoSchema;
    [k: string]: unknown;
  };
}

export type AnimationDirection = 'left' | 'right';

const S = {
  ProductImageGallery: styled.div`
    ${({ theme }) => css`
      --image-background-placeholder: ${theme.colors.cream[100]};
      --width-offset: 0px;

      min-width: 100%;

      @media ${({ theme }) => media(theme).lessThan('lg')} {
        --width-offset: 42px;
      }

      .has-open-sideNavigation & {
        --width-offset: 42px;
      }

      [data-gallery-zoom-in='true'] & {
        --width: 100vw;
      }

      [data-gallery-zoom-in='false'] & {
        --width-1: calc(100% - 360px);
        --width-2: calc(var(--width-offset) + (100% / 3) * 2);
        --width: min(var(--width-1), var(--width-2));
      }

      background-color: ${theme.colors.background.base};
      text-align: center;
      pointer-events: all;
      width: var(--width);
      top: 0;
      z-index: -1;
      align-self: flex-start;

      @media ${({ theme }) => media(theme).noMotionPreference} {
        transition: max-height 0.25s ease;
      }

      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        [data-gallery-zoom-in='true'] & {
          height: calc(100vh - var(--header-height));
          overflow: hidden;
        }
      }

      @media ${({ theme }) => media(theme).lessThan('lg')} {
        --width: 100%;

        position: relative;
        top: initial;
        z-index: initial;
      }

      ${ProductImageGalleryStyles[theme.siteType]}
    `}
  `,
};

type Props = PropsWithChildren<ProductImageGalleryHybrisProps & ProductImageGallerySchema>;

export const ProductImageGallery: FC<Props> = props => {
  const {
    autoSlide,
    closeButtonAriaLabel,
    showMore,
    showLess,
    productVideo,
    productSignings,
    ordering,
    largeDesktopBreakpoint = DEFAULT_LARGE_DESKTOP_BREAKPOINT,
  } = props;
  const {
    product: { videos },
  } = usePDPContext();
  const { galleryZoomIn } = useGalleryContext();
  const { siteType } = useAppContext();

  const [showAllImages, setShowAllImages] = useState(false);

  const { isDesktop } = useMediaQueryContext();
  const { sideNavOpened } = useNavActionContext();
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const imageGalleryWrapperRef = useRef<HTMLDivElement>(null);
  const showMoreButtonRef = useRef<HTMLDivElement>(null);
  const zoomPanelControlsRef = useRef<ProductZoomPanelHandle | null>(null);

  const useUnpackedGallery = CodeFeatureFlag[siteType].unpackedGallery;
  const isUnpackedImagery = useUnpackedGallery && isDesktop;
  const video = videos?.find(v => v?.mode === VideoMode.Landscape);

  const { productImageList, videoIndex } = useProductDetailsImages(ordering);

  const carouselItemsList = useMemo(() => {
    const shouldInsertVideo = Boolean(video);

    if (!shouldInsertVideo || galleryZoomIn) {
      return productImageList;
    }

    const result = [...productImageList];
    const videoPlaceholder = {
      ...productImageList[0],
      isVideo: true,
    };

    result.splice(MOBILE_VIDEO_POSITION, 0, videoPlaceholder);

    return result;
  }, [productImageList, video, galleryZoomIn]);
  const galleryItemsCount = video ? productImageList.length + 1 : productImageList.length;

  const setZoomPanelScrollXCenter = useCallback((e: MouseEvent) => {
    zoomPanelControlsRef.current?.setZoomPanelScrollXCenter(e);
  }, []);

  const imageGalleryViewHandler = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      if (!showMoreButtonRef.current) {
        return;
      }

      const isClickEvent = event?.type === 'click';
      const { height, top } = showMoreButtonRef.current.getBoundingClientRect();

      if (imageGalleryWrapperRef.current?.parentElement) {
        const sideNavWidth =
          sideNavOpened && window.innerWidth >= Number.parseInt(breakpoints.xl, 10)
            ? Number.parseInt(
                window
                  .getComputedStyle(document.documentElement)
                  .getPropertyValue('--side-nav-width'),
                10
              )
            : 0;
        const isSmallDesktopLayout =
          useUnpackedGallery &&
          window.innerWidth >= Number.parseInt(breakpoints.lg, 10) &&
          window.innerWidth < largeDesktopBreakpoint;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const viewPortWidth = window.innerWidth - scrollbarWidth;

        const galleryItemHeight =
          (viewPortWidth - Math.max(viewPortWidth * 0.2, 400) - sideNavWidth) / 2 + 1;

        const galleryWidth = viewPortWidth - Math.max(viewPortWidth * 0.2, 400) - sideNavWidth;

        const rowPattern = getRowPattern(galleryItemsCount);

        const rowHeights = isSmallDesktopLayout
          ? rowPattern.map(row => (row === 1 ? galleryWidth + 1 : galleryWidth / 2 + 1))
          : [];
        const totalRowsHeight = rowHeights.reduce((sum, value) => sum + value, 0);
        const visibleRowsHeight = rowHeights.slice(0, 3).reduce((sum, value) => sum + value, 0);

        let scrollOffset = 0;

        if (isSmallDesktopLayout) {
          if (isClickEvent ? !showAllImages : showAllImages) {
            imageGalleryWrapperRef.current.parentElement.style.height = `${
              totalRowsHeight + height
            }px`;
          } else {
            if (isClickEvent) {
              scrollOffset =
                window.innerHeight / 2 -
                (window.innerHeight - top) -
                Math.max(totalRowsHeight - visibleRowsHeight, 0);

              window.scrollBy({
                top: scrollOffset,
                behavior: 'smooth',
              });
            }

            imageGalleryWrapperRef.current.parentElement.style.height = `${
              Math.min(visibleRowsHeight, totalRowsHeight) + height
            }px`;
          }
        } else if (isClickEvent ? !showAllImages : showAllImages) {
          imageGalleryWrapperRef.current.parentElement.style.height = `${
            Math.ceil(galleryItemsCount / 2) * galleryItemHeight + height
          }px`;
        } else {
          if (isClickEvent) {
            scrollOffset =
              window.innerHeight / 2 -
              (window.innerHeight - top) -
              Math.ceil((galleryItemsCount - 4) / 2) * galleryItemHeight;

            window.scrollBy({
              top: scrollOffset,
              behavior: 'smooth',
            });
          }

          imageGalleryWrapperRef.current.parentElement.style.height = `${
            2 * galleryItemHeight + height
          }px`;
        }
      }
    },
    [
      showMoreButtonRef,
      imageGalleryWrapperRef,
      sideNavOpened,
      showAllImages,
      galleryItemsCount,
      largeDesktopBreakpoint,
      useUnpackedGallery,
    ]
  );

  const handleShowMoreClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const allImageContainers =
        imageGalleryWrapperRef.current?.querySelectorAll('[style*="opacity"]');

      const firstHiddenImage = Array.from(allImageContainers || [])
        .find(container => getComputedStyle(container).opacity === '0')
        ?.querySelector('img[data-image-index]');

      imageGalleryViewHandler(event);
      setShowAllImages(value => !value);

      if (firstHiddenImage instanceof HTMLElement) {
        requestAnimationFrame(() => firstHiddenImage.focus());
      }
    },
    [imageGalleryViewHandler, imageGalleryWrapperRef]
  );

  useLayoutEffect(() => {
    const imageGalleryResizeHandler = () => {
      if (
        window.innerWidth < Number.parseInt(breakpoints.lg, 10) &&
        imageGalleryWrapperRef.current?.parentElement
      ) {
        imageGalleryWrapperRef.current.parentElement.style.height = '';
        setShowAllImages(false);
      } else {
        imageGalleryViewHandler();
      }
    };

    window.addEventListener('resize', imageGalleryResizeHandler);

    return () => window.removeEventListener('resize', imageGalleryResizeHandler);
  }, [imageGalleryViewHandler]);

  useLayoutEffect(() => {
    if (isDesktop) {
      requestAnimationFrame(() => imageGalleryViewHandler());
    }
  }, [isDesktop, sideNavOpened, isUnpackedImagery, imageGalleryViewHandler]);

  const signings = productSignings?.content && <Signings {...productSignings.content} />;

  return (
    <S.ProductImageGallery className="product-image-gallery" ref={imageGalleryWrapperRef}>
      <CSSLargeScreenOnly>
        <ProductScrolledView
          showAllImages={showAllImages}
          productImageList={productImageList}
          setCurrentImageIndex={setCurrentImageIndex}
          setZoomPanelScrollXCenter={setZoomPanelScrollXCenter}
          productVideo={productVideo}
          videoIndex={videoIndex}
          largeDesktopBreakpoint={largeDesktopBreakpoint}
        >
          {signings}
        </ProductScrolledView>

        <FeatureFlag flag="unpackedGallery">
          {galleryItemsCount > 4 && (
            <ProductGalleryShowMoreButton
              buttonRef={showMoreButtonRef}
              showAllImages={showAllImages}
              showMore={showMore}
              showLess={showLess}
              onClick={handleShowMoreClick}
            />
          )}
        </FeatureFlag>
      </CSSLargeScreenOnly>

      <CSSSmallScreenOnly>
        <ProductCarouselVideoView
          autoSlide={autoSlide}
          productImageList={carouselItemsList}
          productVideo={productVideo}
          setZoomPanelScrollXCenter={setZoomPanelScrollXCenter}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
        >
          {signings}
        </ProductCarouselVideoView>
      </CSSSmallScreenOnly>

      <ProductZoomPanel
        productImageList={productImageList}
        currentImageIndex={currentImageIndex}
        closeButtonAriaLabel={closeButtonAriaLabel}
        controlsRef={zoomPanelControlsRef}
      />

      <FeatureFlag flag="styliticsOutfitsRecommendationsEnabled">
        <JumpLink />
      </FeatureFlag>
    </S.ProductImageGallery>
  );
};
